/**
 * MONSTER TOWN — TOWN PROP ART. OWNED BY THE WORLD CREW (src/world/**).
 *
 * The chunky storybook vocabulary the town is dressed with: trees, hedges,
 * lamp posts, benches, rocks, reeds, hills. Every factory returns a Group whose
 * ORIGIN IS ON THE GROUND at (0,0) so callers just position it, and every one
 * is built only from `src/render/kit.ts` (shared cached geometry + materials).
 *
 * These are STATIC SCENERY. They get merged by `mergeStatic()` and are not
 * pickable. The kid-placeable versions of the same idea (a tree they choose to
 * plant, a lamp they buy) live in `src/content/townProps.ts` as real placeables
 * with real interactions — LAW #1 applies there, not here.
 *
 * `inked()` doubles a draw call, so it is used on SILHOUETTES only: tree crowns,
 * building bodies, the things a kid's eye reads as a shape.
 */

import * as THREE from 'three'
import { mix, type PaletteName } from '../core/palette'
import { ball, box, boxOn, cone, cylinder, flat, group, inkAll, noInk } from '../render/kit'

// ---------------------------------------------------------------------------
// plants
// ---------------------------------------------------------------------------

const LEAFY: readonly PaletteName[] = ['leaf', 'leafLight', 'leafDark']

/**
 * ============ ONE TREE FAMILY, AND ONE INK WEIGHT ============
 *
 * ROUND-3 BLOCKER, quoted: "Four inconsistent tree silhouettes and inconsistent
 * ink outlines in a single frame … a blobby pale-green sphere cluster, a
 * stepped-pyramid conifer, a rounded olive blob, and a stepped-cube hedge — four
 * different art languages for 'tree' in one screenshot. The brown stumps carry
 * no ink outline while the hedges and buildings do."
 *
 * Both halves were real, and both are structural rather than cosmetic:
 *
 * 1. SHAPE. `tree()`, `pineTree()` and `hedge()` were stepped boxes; `bush()`
 *    and `stump()` were spheres and cylinders. A bush is scattered at up to
 *    1.45x scale, so the "blob" was frequently tree-sized and stood next to a
 *    stepped tree at the same height. EVERYTHING in this family is now built
 *    from the same axis-aligned, slightly-turned boxes — a broad tree is three
 *    wide steps, a pine is four narrow ones, a bush is two, a stump is one and
 *    a lid. Trunks are boxes too: a 6-segment cylinder's silhouette is a
 *    different language from the boxes stacked on it.
 *
 * 2. INK. `inkAll` puts the shell out by a fixed number of WORLD units, and the
 *    scenery scatter then scales the whole prop by 0.62..1.45 — so a small tree
 *    carried a 0.022-unit line and a full-size hedge carried 0.035. At town zoom
 *    that is roughly one device pixel against two, i.e. exactly the reported
 *    "some things are outlined and some are not". Every scenery factory now
 *    takes its final `scale` and divides it back out of the ink width, so the
 *    line is the SAME number of screen pixels on every prop in the frame.
 *    The dark-brown parts also went up a shade (`trunk`, not `trunkDark`), because
 *    a #2B2438 line on a #6A4229 stump is invisible whatever its width.
 */

/**
 * Ink weight for town scenery, in world units. Heavier than the kit default:
 * these are small props read at a 32-unit-tall camera box, where the kit's
 * 0.035 is a sub-pixel hairline on anything that is not a big pale slab.
 */
const SCENERY_INK = 0.055

/**
 * Ink a scenery prop for its FINAL on-screen size, then scale it.
 *
 * `inkAll` measures in the prop's local units, so dividing the width by the
 * scale that is about to be applied leaves exactly `SCENERY_INK` world units of
 * line whatever size the prop ends up. Callers must not scale the result again.
 */
function inkScaled<T extends THREE.Object3D>(g: T, scale: number): T {
  inkAll(g, SCENERY_INK / Math.max(0.05, scale))
  if (scale !== 1) g.scale.setScalar(scale)
  return g
}

/**
 * The top step's colour.
 *
 * A TINTED PROP KEEPS ONE COLOUR, DELIBERATELY. Lightening the cap of every
 * tinted tree looked better in isolation and cost a NEW MATERIAL per tint —
 * `mergeStatic` buckets by material, so six leaf tints turned into twelve
 * buckets and twelve draw calls per greenery cluster on the machine that
 * decides. The scenery pine has always drawn its four steps in one tint and
 * reads perfectly well, because the steps are what make the shape; the untinted
 * props (the park trees, the corner-garden shrubs) still get the lighter cap.
 */
function capColor(tint: PaletteName | number | undefined, fallback: PaletteName): number | PaletteName {
  return tint ?? fallback
}

/** The family's trunk: a chunky turned box, never a cylinder. */
function trunkBox(g: THREE.Group, w: number, h: number, twist: number, color: PaletteName): void {
  const t = boxOn(w, h, w, { color })
  t.rotation.y = twist
  g.add(t)
  const flare = boxOn(w * 1.42, 0.3, w * 1.42, { color: 'trunkDark' })
  flare.rotation.y = twist
  g.add(flare)
}

/**
 * Round storybook tree. `v` (0..1) varies height, tilt and leaf shade.
 * `scale` is the size it will be planted at — see `inkScaled`.
 */
export function tree(v = 0.5, tint?: PaletteName | number, scale = 1): THREE.Group {
  const g = group('tree')
  const h = 2.0 + v * 1.4
  const leaf = tint ?? LEAFY[Math.floor(v * LEAFY.length) % LEAFY.length] ?? 'leaf'
  const cap = capColor(tint, 'leafLight')
  trunkBox(g, 0.62, h, v * 0.9, 'trunk')
  const crown = boxOn(3.3 + v * 0.9, 1.7, 3.3 + v * 0.9, { color: leaf, y: h - 0.2 })
  crown.rotation.y = v * 0.9
  g.add(crown)
  const mid = boxOn(2.1, 1.2, 2.1, { color: cap, y: h + 1.35 })
  mid.rotation.y = 0.4 - v * 0.7
  g.add(mid)
  g.add(boxOn(1.1, 0.7, 1.1, { color: leaf, y: h + 2.3, x: 0.2 - v * 0.4 }))
  return inkScaled(g, scale)
}

/**
 * Tall tree — reads as "forest". Same stepped-box family as `tree()`, just
 * narrower and taller. `skyline` drops the outline for the fogged far ring,
 * where an ink line is invisible and only costs triangles.
 */
export function pineTree(
  v = 0.5,
  skyline = false,
  tint?: PaletteName | number,
  scale = 1,
): THREE.Group {
  const g = group('pine')
  const h = 3.0 + v * 1.6
  const twist = v * 0.8
  // The trunk only takes the tint on the SKYLINE ring, where the whole tree is
  // one hazed silhouette. A near pine with a green trunk is a bug, not variety.
  trunkBox(g, 0.56, h * 0.42, twist, skyline && tint !== undefined ? 'trunkDark' : 'trunk')
  const steps: readonly [number, number, number, PaletteName][] = [
    [2.3 + v * 0.5, 1.15, h * 0.3, 'leafDark'],
    [1.75 + v * 0.4, 1.0, h * 0.3 + 0.95, 'leaf'],
    [1.2 + v * 0.3, 0.85, h * 0.3 + 1.8, 'leafLight'],
    [0.66, 0.62, h * 0.3 + 2.5, 'leaf'],
  ]
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i]
    if (s === undefined) continue
    const m = boxOn(s[0], s[1], s[0], { color: tint ?? s[3], y: s[2] })
    m.rotation.y = twist + i * 0.22
    g.add(m)
  }
  // The fogged far ring drops the ink: at 50+ units the line is sub-pixel and
  // only costs triangles. Everything a kid can walk near keeps it.
  if (skyline) {
    if (scale !== 1) g.scale.setScalar(scale)
    return g
  }
  return inkScaled(g, scale)
}

/**
 * A shrub. TWO STEPPED BOXES — the same vocabulary as the trees above it, at
 * knee height. This was three overlapping spheres, which is what the round-3
 * critic photographed as "a blobby pale-green sphere cluster" standing in the
 * same frame as a stepped tree.
 */
export function bush(v = 0.5, tint?: PaletteName | number, scale = 1): THREE.Group {
  const g = group('bush')
  const w = 1.5 + v * 0.55
  const hh = 0.66 + v * 0.22
  const base = boxOn(w, hh, w * 0.88, { color: tint ?? 'leaf' })
  base.rotation.y = v * 0.9
  g.add(base)
  const cap = boxOn(w * 0.66, 0.44, w * 0.6, { color: capColor(tint, 'leafLight'), y: hh - 0.06 })
  cap.rotation.y = 0.5 - v * 0.8
  g.add(cap)
  return inkScaled(g, scale)
}

/**
 * A cut stump. Exists purely so the tree belt is not forty copies of one mesh
 * on a lattice — the critic's "reads as a tiling pattern rather than woodland".
 *
 * Box-built and a shade LIGHTER than it was: a `trunkDark` (#6A4229) cylinder
 * with a #2B2438 outline is the "brown stumps carry no ink outline" defect even
 * when the outline is there, because the line and the wood are the same value.
 */
export function stump(v = 0.5, scale = 1): THREE.Group {
  const g = group('stump')
  /*
   * SMALL AND LOW. The first box-built pass kept the old cylinder's footprint
   * (1.3 across, 0.9 tall) and the captured frame read it exactly as you would
   * expect a waist-high box with a pale flat top to read: a wooden table. A
   * stump is ankle height and barely wider than the trunk that was cut off it.
   */
  const w = 0.72 + v * 0.22
  const h = 0.32 + v * 0.18
  const twist = v * 1.2
  const body = boxOn(w, h, w * 0.9, { color: 'trunk' })
  body.rotation.y = twist
  g.add(body)
  // The cut face is a shade LIGHTER than the bark — that is what says "sawn" —
  // but only a shade. `woodLight` on top made it a tabletop.
  const lid = boxOn(w * 0.97, 0.1, w * 0.87, { color: 'wood', y: h - 0.03 })
  lid.rotation.y = twist
  g.add(lid)
  // A tuft of moss banked against one side, sitting on the ground and clear of
  // the lid, so a stump is never a plain brown box.
  const moss = boxOn(w * 0.52, h * 0.62, w * 0.44, { color: 'leaf', x: w * 0.56, z: -w * 0.22 })
  moss.rotation.y = twist + 0.4
  g.add(moss)
  return inkScaled(g, scale)
}

/** A clump of flowers for the roadside. Cheap: no outlines. */
export function flowerClump(v = 0.5, color: PaletteName = 'flowerPink'): THREE.Group {
  const g = group('flowers')
  const spots: readonly [number, number][] = [
    [-0.3, -0.2],
    [0.25, 0.15],
    [0.05, 0.35],
  ]
  for (let i = 0; i < spots.length; i++) {
    const s = spots[i]
    if (s === undefined) continue
    const h = 0.45 + ((i + v) % 1) * 0.3
    g.add(cylinder(0.05, h, { color: 'leaf', x: s[0], z: s[1], seg: 5 }))
    g.add(ball(0.18, { color, x: s[0], y: h, z: s[1], seg: 6 }))
    g.add(ball(0.08, { color: 'flowerYellow', x: s[0], y: h + 0.09, z: s[1], seg: 5 }))
  }
  return g
}

/** One tile of hedge. Butts up against its neighbours to read as a run. */
export function hedge(length = 2, v = 0.5): THREE.Group {
  const g = group('hedge')
  g.add(boxOn(length, 1.05 + v * 0.12, 0.9, { color: 'leafDark' }))
  g.add(boxOn(length * 0.86, 0.22, 0.72, { color: 'leaf', y: 1.0 + v * 0.12 }))
  return inkAll(g)
}

/**
 * A tuft of reeds standing IN water.
 *
 * Round 2 built these from five thin 0.12 boxes fanned round a circle, which at
 * this camera read as "flat crossed planes" — no volume, no clump. A reed bed is
 * a dense cluster of tapered stems with a couple of brown cattail heads, so it
 * is now built like one: eight stems of varying height leaning off a common
 * base, plus seed heads. The caller is responsible for only ever planting it in
 * the water (the other half of that defect).
 */
export function reeds(v = 0.5): THREE.Group {
  const g = group('reeds')
  const n = 5
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + v * 3
    const rad = 0.1 + ((i * 3 + v * 7) % 4) * 0.075
    const h = 0.75 + ((i * 7 + v * 10) % 5) * 0.22
    const x = Math.cos(a) * rad
    const z = Math.sin(a) * rad
    const stem = cylinder(0.055 + (i % 2) * 0.015, h, {
      color: i % 3 === 0 ? 'leafDark' : 'leaf',
      x,
      z,
      seg: 5,
    })
    // Reeds lean. A vertical bundle is a fence; a leaning one is a plant.
    stem.rotation.z = Math.cos(a) * 0.16
    stem.rotation.x = -Math.sin(a) * 0.16
    g.add(stem)
    if (i % 3 === 1) {
      g.add(
        cylinder(0.085, 0.3, {
          color: 'trunkDark',
          x: x + Math.cos(a) * h * 0.1,
          y: h - 0.06,
          z: z + Math.sin(a) * h * 0.1,
          seg: 5,
        }),
      )
    }
  }
  // A little bed of flatter leaves at the waterline, so the stems come OUT of
  // something instead of ending at a hard plane.
  g.add(ball(0.3, { color: 'leafDark', y: 0.04, seg: 6 }))
  return inkAll(g)
}

/**
 * Lily pad — a disc with a real notch cut out of it and a slight tilt, floating
 * ON the surface rather than lying flat like a hexagon decal.
 */
export function lilyPad(v = 0.5): THREE.Group {
  const g = group('lily')
  const r = 0.34 + v * 0.12
  const a = v * 6.2
  g.add(cylinder(r, 0.07, { color: 'leafLight', seg: 12 }))
  // The notch: one small water-dark wedge cut into the RIM. Small, and at the
  // edge — a wedge near the middle plus a second disc on top of it turns the pad
  // into a target, which is not what a lily pad looks like.
  g.add(
    boxOn(r * 0.5, 0.085, r * 0.34, {
      color: 'waterDeep',
      x: Math.cos(a) * r * 0.86,
      z: Math.sin(a) * r * 0.86,
      rotY: a,
      y: -0.008,
    }),
  )
  if (v > 0.62) {
    g.add(ball(0.12, { color: 'flowerWhite', y: 0.16, seg: 7 }))
    g.add(ball(0.06, { color: 'flowerYellow', y: 0.24, seg: 5 }))
  }
  // Pads sit at slightly different angles on the ripple.
  g.rotation.x = (v - 0.5) * 0.12
  g.rotation.z = (0.5 - v) * 0.14
  return inkAll(g)
}

// ---------------------------------------------------------------------------
// rock / ground detail
// ---------------------------------------------------------------------------

/**
 * A boulder. INKED like everything else — round 2 shipped these as bare grey
 * boxes standing beside inked trees and benches, which is precisely the
 * "inconsistent outline pass" the critic called out. `sink` pushes it into the
 * ground so it is bedded rather than placed.
 */
export function rock(v = 0.5): THREE.Group {
  const g = group('rock')
  const s = 0.55 + v * 0.6
  // WARM STONE, not concrete (vr2). See the note beside `stone` in palette.ts:
  // seven neutral-grey slabs round the pond were the only pure greys in a warm
  // town, and the critic read them as unfinished rather than as rocks.
  const shade: PaletteName = v > 0.6 ? 'stoneDark' : 'stone'
  const body = boxOn(s * 1.4, s * 0.85, s * 1.1, { color: shade, rotY: v * 1.4 })
  // A rock that sits perfectly square reads as a crate; tip it a little.
  body.rotation.z = 0.1 - v * 0.2
  body.rotation.x = v * 0.16 - 0.08
  g.add(body)
  const chip = boxOn(s * 0.62, s * 0.44, s * 0.55, {
    color: shade === 'stone' ? 'stoneDark' : 'stone',
    x: s * 0.55,
    z: s * 0.32,
    rotY: -v,
  })
  chip.rotation.z = -0.16
  g.add(chip)
  g.add(ball(s * 0.4, { color: shade, x: -s * 0.42, y: s * 0.28, z: -s * 0.22, seg: 7 }))
  inkAll(g)
  // Bedded, not balanced: a boulder's bottom belongs under the ground line.
  g.position.y = -s * 0.22
  return g
}

/**
 * A RAISED FLOWER BED with real flowers in it.
 *
 * Round 2's garden was a brown quad with flat coloured squares painted on it —
 * "2D sticky notes in a 3D world", sitting on the same lawn as the fully
 * modelled window boxes on the houses, so the world contradicted itself about
 * what a flower is. This is the same idea built out of the same vocabulary as
 * the planters: timber edging, a mound of soil, stems, blooms and leaves.
 */
export function flowerBed(
  v = 0.5,
  colors: readonly PaletteName[] = ['flowerPink', 'flowerYellow', 'flowerPurple'],
  size = 2.6,
): THREE.Group {
  const g = group('flowerBed')
  const half = size / 2
  const rail = 0.26
  // Timber edging on all four sides, corners joined (LAW #5: no open seams).
  for (const s of [-1, 1]) {
    g.add(boxOn(size + rail, rail, rail, { color: 'wood', z: s * half }))
    g.add(boxOn(rail, rail, size + rail, { color: 'wood', x: s * half }))
  }
  // Soil, proud of the timber so the bed reads as filled rather than empty.
  g.add(boxOn(size - 0.04, 0.22, size - 0.04, { color: 'dirtDark' }))
  g.add(boxOn(size - 0.34, 0.08, size - 0.34, { color: 'dirt', y: 0.22 }))

  let seed = Math.floor(v * 9973) + 17
  const rnd = (): number => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
    return seed / 0x100000000
  }
  const n = 6 + Math.floor(rnd() * 4)
  for (let i = 0; i < n; i++) {
    const px = (rnd() - 0.5) * (size - 0.7)
    const pz = (rnd() - 0.5) * (size - 0.7)
    const h = 0.34 + rnd() * 0.28
    const c = colors[Math.floor(rnd() * colors.length)] ?? 'flowerPink'
    g.add(cylinder(0.05, h, { color: 'leaf', x: px, y: 0.3, z: pz, seg: 5 }))
    g.add(ball(0.05, { color: 'leafDark', x: px + 0.11, y: 0.3 + h * 0.55, z: pz, seg: 5 }))
    g.add(ball(0.19, { color: c, x: px, y: 0.3 + h, z: pz, seg: 7 }))
    g.add(ball(0.08, { color: 'flowerYellow', x: px, y: 0.3 + h + 0.09, z: pz, seg: 5 }))
  }
  return inkAll(g)
}

/** Distant hill. Flat-shaded, never casts shadows — it is pure skyline. */
export function hill(
  radius: number,
  height: number,
  color: PaletteName | number = 'grassDark',
): THREE.Group {
  const g = group('hill')
  const m = cone(radius, height, { color, seg: 7, flat: true })
  m.castShadow = false
  m.receiveShadow = false
  g.add(m)
  return g
}

// ---------------------------------------------------------------------------
// street furniture
// ---------------------------------------------------------------------------

/** Lamp post. The bulb is unlit `flat()` so it reads as glowing in daylight. */
export function lampPost(): THREE.Group {
  const g = group('lamp')
  g.add(boxOn(0.52, 0.22, 0.52, { color: 'rockDark' }))
  g.add(cylinder(0.11, 2.6, { color: 'metalDark', y: 0.18, seg: 6 }))
  g.add(boxOn(0.42, 0.2, 0.42, { color: 'metalDark', y: 2.7 }))
  const bulb = boxOn(0.56, 0.52, 0.56, { color: 'sunWarm', y: 2.88 })
  bulb.material = flat('sunWarm')
  bulb.castShadow = false
  noInk(bulb)
  g.add(cone(0.44, 0.34, { color: 'roofRed', y: 3.38, seg: 6 }))
  inkAll(g)
  // The bulb is added AFTER the ink pass so the glow is never boxed in.
  g.add(bulb)
  return g
}

export function bench(): THREE.Group {
  const g = group('bench')
  for (const x of [-0.85, 0.85]) {
    g.add(boxOn(0.22, 0.5, 1.0, { color: 'metalDark', x }))
  }
  g.add(boxOn(2.3, 0.18, 1.0, { color: 'woodLight', y: 0.5 }))
  const back = boxOn(2.3, 0.7, 0.18, { color: 'wood', y: 0.68, z: -0.42 })
  back.rotation.x = -0.12
  g.add(back)
  return inkAll(g)
}

export function mailbox(): THREE.Group {
  const g = group('mailbox')
  g.add(cylinder(0.12, 1.15, { color: 'woodDark', seg: 6 }))
  g.add(box(0.62, 0.5, 0.86, { color: 'roofTeal', y: 1.4, name: 'boxPart' }))
  g.add(box(0.1, 0.34, 0.1, { color: 'uiDanger', x: 0.36, y: 1.62, z: -0.2, name: 'flag' }))
  return inkAll(g)
}

/**
 * A picture sign on a post. No words anywhere in this game (LAW #4) — the icon
 * is built out of geometry so a 3-year-old reads it at a glance.
 */
export type SignIcon = 'home' | 'cone' | 'plate' | 'coin' | 'tree'

/**
 * THE ONE COLOUR EVERY SIGNBOARD IS PAINTED, and it is not cream.
 *
 * ROUND-3 BLOCKER: "a blank signboard placed in the street with no icon on it".
 * The icon was there. The board's inset panel was `wallCream` (#FFEFD6) and the
 * `home` picture's house body is `wallCream` too, so the only part of the sign
 * that survived was the little red roof floating on an otherwise empty plaque —
 * which is precisely what a critic (and a kid) reads as an unfinished sign.
 *
 * One cool board colour behind every picture fixes it for good, and by
 * construction rather than by luck: cream house, gold coin, white plate, pink
 * cone and green tree all separate from `wallBlue`, and no future icon can
 * collide with the board by sharing a wall colour with it. It also ties the
 * physical signposts to the teal HUD chips that name the same places.
 */
export const SIGN_BOARD: PaletteName = 'wallBlue'

export function signIcon(kind: SignIcon): THREE.Group {
  const g = group(`icon:${kind}`)
  switch (kind) {
    case 'home': {
      g.add(box(0.9, 0.7, 0.16, { color: 'wallCream' }))
      // kit's cone() ignores rotY, so the 45-degree "pyramid roof" turn is manual.
      const roof = cone(0.78, 0.55, { color: 'roofRed', y: 0.3, seg: 4 })
      roof.rotation.y = Math.PI / 4
      g.add(roof)
      g.add(box(0.26, 0.34, 0.2, { color: 'doorWood', y: -0.18 }))
      break
    }
    case 'cone':
      g.add(cone(0.34, 0.8, { color: 'cone', y: -0.62, seg: 8 }).rotateZ(Math.PI))
      g.add(ball(0.32, { color: 'iceStrawberry', y: 0.16, seg: 8 }))
      g.add(ball(0.24, { color: 'iceVanilla', y: 0.5, seg: 8 }))
      g.add(ball(0.11, { color: 'cherry', y: 0.72, seg: 6 }))
      break
    case 'plate':
      g.add(cylinder(0.52, 0.12, { color: 'plate', y: -0.06, seg: 10 }))
      g.add(ball(0.2, { color: 'iceChocolate', y: 0.16, seg: 8 }))
      g.add(box(0.1, 0.7, 0.1, { color: 'metal', x: -0.62, y: 0.1 }))
      g.add(box(0.1, 0.7, 0.1, { color: 'metal', x: 0.62, y: 0.1 }))
      break
    case 'coin':
      g.add(cylinder(0.46, 0.16, { color: 'uiCoin', y: -0.08, seg: 12 }).rotateX(Math.PI / 2))
      g.add(cylinder(0.3, 0.2, { color: 'uiCoinDark', y: -0.1, seg: 12 }).rotateX(Math.PI / 2))
      break
    case 'tree':
      g.add(box(0.2, 0.5, 0.2, { color: 'trunk', y: -0.35 }))
      g.add(ball(0.42, { color: 'leaf', y: 0.18, seg: 8 }))
      break
  }
  return g
}

/**
 * Roadside signpost carrying a picture.
 *
 * TWO-SIDED, DELIBERATELY. Round 1 put the icon on the +Z face only, so the
 * fixed iso camera saw the blank back of roughly half the signs in town and the
 * critic (correctly) read them as unfinished placeholder plaques. There is no
 * free orbit here — a kid cannot walk round to the front — so every sign now
 * carries its picture on BOTH faces, and the board itself gets a coloured inset
 * panel so even an edge-on sign never reads as a blank white rectangle.
 */
export function signPost(kind: SignIcon, boardColor: PaletteName = SIGN_BOARD): THREE.Group {
  const g = group('signPost')
  g.add(cylinder(0.14, 2.2, { color: 'wood', seg: 6 }))
  const board = box(1.9, 1.5, 0.22, { color: 'wood', y: 2.5 })
  g.add(board)
  for (const s of [1, -1]) {
    // Coloured face panel, proud of the frame — the "this is a sign" cue.
    g.add(box(1.6, 1.22, 0.1, { color: boardColor, y: 2.5, z: s * 0.14 }))
    const icon = signIcon(kind)
    icon.position.set(0, 2.5, s * 0.28)
    // Turned, never mirrored: a negative scale flips the winding order and the
    // whole icon renders inside-out under backface culling.
    icon.rotation.y = s > 0 ? 0 : Math.PI
    icon.scale.setScalar(0.8)
    g.add(icon)
  }
  return inkAll(g)
}

/** Wooden fence run of `n` panels, centred, running along X. */
export function fenceRun(n: number, spacing = 2): THREE.Group {
  const g = group('fence')
  const total = n * spacing
  for (let i = 0; i <= n; i++) {
    g.add(boxOn(0.22, 1.15, 0.22, { color: 'woodDark', x: -total / 2 + i * spacing }))
  }
  for (const y of [0.45, 0.85]) {
    g.add(box(total, 0.16, 0.14, { color: 'woodLight', y }))
  }
  return inkAll(g)
}

/** Flower planter box — the thing that makes a shop front look loved. */
export function planter(color: PaletteName = 'flowerPink'): THREE.Group {
  const g = group('planter')
  g.add(boxOn(1.7, 0.5, 0.7, { color: 'wood' }))
  g.add(boxOn(1.5, 0.14, 0.55, { color: 'wood', y: 0.46 }))
  for (const x of [-0.5, 0, 0.5]) {
    g.add(ball(0.26, { color: 'leaf', x, y: 0.68, seg: 6 }))
    g.add(ball(0.16, { color, x, y: 0.86, seg: 6 }))
  }
  return inkAll(g)
}

/**
 * Little wooden jetty over the pond.
 *
 * Legs run the WHOLE length including both ends. Round 1 spaced three pairs
 * across the span and left the far end hanging unsupported over open water,
 * which reads as a bug rather than a jetty. It also gets a end rail so the
 * decking has somewhere to stop.
 */
export function dock(length = 5): THREE.Group {
  const g = group('dock')
  const deckY = 0.34
  const deckT = 0.16
  /*
   * REAL PILINGS, DRIVEN INTO THE POND FLOOR.
   *
   * Round 2's jetty was "a plank slab floating over the water with a drop
   * shadow, no pilings, its far end overhanging the sand, its railing post
   * detached from the deck". The legs used to be 0.62 stubs starting at y=0 —
   * i.e. resting ON the water surface. They now start BELOW the water line and
   * run up THROUGH the deck, which is both how a jetty is built and the only
   * way it reads as standing in water rather than hovering over it.
   */
  const pairs = Math.max(3, Math.round(length / 1.5))
  for (let i = 0; i <= pairs; i++) {
    const z = -length / 2 + 0.3 + (i / pairs) * (length - 0.6)
    for (const x of [-0.72, 0.72]) {
      g.add(cylinder(0.15, deckY + 0.62, { color: 'woodDark', x, y: -0.5, z, seg: 6 }))
    }
  }
  // Deck planks, laid across, with a small gap that catches the ink line.
  const planks = Math.max(4, Math.round(length / 0.62))
  const pw = length / planks
  for (let i = 0; i < planks; i++) {
    g.add(
      boxOn(1.82, deckT, pw * 0.86, {
        color: i % 2 === 0 ? 'woodLight' : 'wood',
        y: deckY,
        z: -length / 2 + pw * (i + 0.5),
      }),
    )
  }
  // Stringers under the deck, so it is carried by the pilings, not floating.
  for (const x of [-0.72, 0.72]) {
    g.add(boxOn(0.18, 0.16, length, { color: 'woodDark', x, y: deckY - 0.16 }))
  }
  /*
   * The end rail, JOINED. The posts sit on the deck line and the top rail runs
   * post-to-post at post height, so nothing is left hanging in the air.
   */
  const railZ = -length / 2 + 0.22
  const postH = 0.86
  for (const x of [-0.72, 0.72]) {
    g.add(boxOn(0.22, postH, 0.22, { color: 'woodDark', x, y: deckY + deckT, z: railZ }))
  }
  g.add(
    boxOn(1.9, 0.16, 0.2, { color: 'woodLight', y: deckY + deckT + postH - 0.16, z: railZ }),
  )
  g.add(
    boxOn(1.66, 0.12, 0.14, { color: 'wood', y: deckY + deckT + postH * 0.48, z: railZ }),
  )
  return inkAll(g)
}

/**
 * A small stone fountain — the town plaza's focal point. Round 2's centre was
 * "a large bare tan cross with no fountain, plaza feature or focal point".
 */
export function fountain(): THREE.Group {
  const g = group('fountain')
  g.add(cylinder(2.05, 0.34, { color: 'rock', seg: 14 }))
  g.add(cylinder(1.78, 0.3, { color: 'rockDark', y: 0.06, seg: 14 }))
  const water = cylinder(1.68, 0.14, { color: 'water', y: 0.3, seg: 14 })
  water.material = flat(mix('water', 'flowerWhite', 0.12), 0.85)
  noInk(water)
  g.add(cylinder(0.62, 0.9, { color: 'rock', y: 0.3, seg: 10 }))
  g.add(cylinder(0.95, 0.18, { color: 'rock', y: 1.2, seg: 12 }))
  g.add(cylinder(0.24, 0.5, { color: 'rockDark', y: 1.38, seg: 8 }))
  const top = ball(0.34, { color: 'water', y: 2.02, seg: 8 })
  top.material = flat(mix('water', 'flowerWhite', 0.3), 0.8)
  top.castShadow = false
  noInk(top)
  inkAll(g)
  g.add(water)
  g.add(top)
  return g
}
